'use client';

/**
 * Architecture Explorer Panel
 *
 * @component_role Architecture Tree Exploration Interface
 * @business_rule Provides hierarchical view of system capabilities.
 *   Lazy-loads children on expand for performance.
 *   Supports search across capabilities, features, and stories.
 * @user_outcome Explore and understand system architecture interactively.
 * @integration_point klyde_edge /docs/arch/tree - architecture tree
 *
 * @layer presentation
 * @arch_component_type panel
 * @arch_domain docs
 */

import { useState, useCallback } from 'react';
import {
  ChevronRight,
  ChevronDown,
  Loader2,
  Search,
  Box,
  Layers,
  Zap,
  FileText,
  BookOpen,
} from 'lucide-react';
import { useArchTree, useArchSearch } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { api } from '@/lib/api/client';
import { cn } from '@/lib/utils/cn';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import type { ArchTreeNode, ArchNodeType, ArchChildrenResponse } from '@/lib/api/types';

// Icon component for node types
function NodeIcon({ type, className }: { type: string; className?: string }) {
  const colors: Record<string, string> = {
    system: 'text-purple-500',
    package: 'text-blue-500',
    capability: 'text-amber-500',
    feature: 'text-green-500',
    story: 'text-gray-500',
  };
  const color = colors[type] || 'text-gray-500';
  const combinedClass = cn(className, color);

  switch (type) {
    case 'system':
      return <Box className={combinedClass} />;
    case 'package':
      return <Layers className={combinedClass} />;
    case 'capability':
      return <Zap className={combinedClass} />;
    case 'feature':
      return <FileText className={combinedClass} />;
    case 'story':
      return <BookOpen className={combinedClass} />;
    default:
      return <FileText className={combinedClass} />;
  }
}

interface TreeNodeProps {
  node: ArchTreeNode;
  level: number;
  onNodeClick?: (node: ArchTreeNode) => void;
}

function TreeNodeComponent({ node, level, onNodeClick }: TreeNodeProps) {
  const [isExpanded, setIsExpanded] = useState(level < 2); // Auto-expand first 2 levels
  const [children, setChildren] = useState<ArchTreeNode[] | null>(node.children);
  const [isLoading, setIsLoading] = useState(false);
  const treeChildrenStrategy = useCapabilityStore((s) => s.strategyName('docs', 'tree_children'));

  const hasChildren = node.child_count > 0 || (children && children.length > 0);
  const isLeaf = node.child_count === 0 && !children?.length;

  const handleExpand = useCallback(async () => {
    if (isLeaf) return;

    if (!isExpanded && children === null) {
      // Need to lazy-load children
      setIsLoading(true);
      try {
        const res = await api.getStrategyData<ArchChildrenResponse>(treeChildrenStrategy!, { semantic_id: node.id });
        if (!res.success) throw new Error(res.error || `${treeChildrenStrategy} failed`);
        setChildren(res.data.children);
        setIsExpanded(true);
      } catch (error) {
        console.error('Failed to load children:', error);
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsExpanded(!isExpanded);
    }
  }, [isExpanded, children, node.id, isLeaf, treeChildrenStrategy]);

  const handleClick = useCallback(() => {
    onNodeClick?.(node);
  }, [node, onNodeClick]);

  return (
    <div className="select-none">
      {/* Node row */}
      <div
        className={cn(
          'flex items-center gap-1 py-1 px-2 rounded-sm cursor-pointer',
          'hover:bg-accent/50 transition-colors',
          level === 0 && 'font-medium'
        )}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
      >
        {/* Expand/collapse button */}
        <button
          onClick={handleExpand}
          className={cn(
            'w-4 h-4 flex items-center justify-center shrink-0',
            isLeaf && 'invisible'
          )}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
          ) : isExpanded ? (
            <ChevronDown className="w-3 h-3 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-3 h-3 text-muted-foreground" />
          )}
        </button>

        {/* Icon */}
        <NodeIcon type={node.type} className="w-4 h-4 shrink-0" />

        {/* Label */}
        <span
          onClick={handleClick}
          className="truncate flex-1 text-sm"
          title={node.description || node.name}
        >
          {node.name}
        </span>

        {/* Child count badge */}
        {node.child_count > 0 && !isExpanded && (
          <span className="text-xs text-muted-foreground bg-muted px-1.5 rounded">
            {node.child_count}
          </span>
        )}
      </div>

      {/* Children */}
      {isExpanded && children && children.length > 0 && (
        <div>
          {children.map((child) => (
            <TreeNodeComponent
              key={child.id}
              node={child}
              level={level + 1}
              onNodeClick={onNodeClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function ArchExplorer() {
  const [searchQuery, setSearchQuery] = useState('');
  const selectNode = useGraphSelectionStore((state) => state.selectNode);
  const selectedNode = useGraphSelectionStore((state) => state.selectedNode);

  const archTreeStrategy = useCapabilityStore((s) => s.strategyName('docs', 'tree'));
  const { data: treeData, isLoading: isTreeLoading, error: treeError } = useArchTree(archTreeStrategy!);
  const search = useArchSearch();

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (searchQuery.trim()) {
        search.mutate({ query: searchQuery.trim(), limit: 10 });
      }
    },
    [searchQuery, search]
  );

  const handleNodeClick = useCallback((node: ArchTreeNode) => {
    // Select node in global store - this drives copilot panel tabs
    selectNode({
      id: node.id,
      label: node.name,
      type: node.type,
      properties: {
        description: node.description,
        child_count: node.child_count,
        // Include story-specific properties if present
        ...(node.extra || {}),
      },
    });
  }, [selectNode]);

  // Handle search result click
  const handleSearchResultClick = useCallback((result: { semantic_id: string; name: string; label: string; description?: string }) => {
    selectNode({
      id: result.semantic_id,
      label: result.name,
      type: result.label,
      properties: {
        description: result.description,
      },
    });
  }, [selectNode]);

  return (
    <div className="flex flex-col h-full">
      {/* Search bar */}
      <div className="p-3 border-b border-border">
        <form onSubmit={handleSearch}>
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search architecture..."
              className={cn(
                'w-full pl-9 pr-4 py-2 text-sm rounded-md',
                'bg-muted border border-border',
                'focus:outline-none focus:ring-1 focus:ring-primary',
                'placeholder:text-muted-foreground'
              )}
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          </div>
        </form>

        {/* Search results */}
        {search.data && search.data.count > 0 && (
          <div className="mt-2 text-xs text-muted-foreground">
            {search.data.count} results for &quot;{search.data.query}&quot;
          </div>
        )}
      </div>

      {/* Tree or search results */}
      <div className="flex-1 overflow-auto">
        {/* Search results view */}
        {search.data && search.data.results.length > 0 ? (
          <div className="py-2">
            {search.data.results.map((result) => (
              <div
                key={result.semantic_id}
                onClick={() => handleSearchResultClick(result)}
                className={cn(
                  'flex items-start gap-2 px-3 py-2',
                  'hover:bg-accent/50 transition-colors cursor-pointer',
                  selectedNode?.id === result.semantic_id && 'bg-accent'
                )}
              >
                <NodeIcon type={result.label} className="w-4 h-4 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{result.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {result.description || result.label}
                  </p>
                </div>
                <span className="text-xs text-muted-foreground">
                  {Math.round(result.score * 100)}%
                </span>
              </div>
            ))}
            <button
              onClick={() => search.reset()}
              className="w-full px-3 py-2 text-xs text-muted-foreground hover:text-foreground"
            >
              Clear search
            </button>
          </div>
        ) : isTreeLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : treeError ? (
          <div className="p-4 text-center text-sm text-destructive">
            Failed to load architecture tree
          </div>
        ) : treeData?.root ? (
          <div className="py-2">
            <TreeNodeComponent
              node={treeData.root}
              level={0}
              onNodeClick={handleNodeClick}
            />
          </div>
        ) : (
          <div className="p-4 text-center text-sm text-muted-foreground">
            <Box className="w-8 h-8 mx-auto mb-2 text-primary/50" />
            <p>No architecture data available</p>
            <p className="text-xs mt-1">Run sync to populate the graph</p>
          </div>
        )}
      </div>

      {/* Selected node details */}
      {selectedNode && (
        <div className="border-t border-border p-3">
          <div className="flex items-center gap-2 mb-1">
            <NodeIcon type={selectedNode.type} className="w-4 h-4" />
            <span className="text-sm font-medium truncate">{selectedNode.label}</span>
          </div>
          {selectedNode.properties?.description ? (
            <p className="text-xs text-muted-foreground line-clamp-3">
              {String(selectedNode.properties.description)}
            </p>
          ) : null}
          <div className="mt-1 text-xs text-muted-foreground">
            {selectedNode.type} &middot; {selectedNode.id}
          </div>
        </div>
      )}
    </div>
  );
}
